import { useEffect, useRef, useState } from 'react'
import type { CardCopy } from '../lib/hubCopy'
import {
  cropRect,
  decodePhoto,
  loadMyPhoto,
  photoDataUrl,
  removeProfilePhoto,
  renderCrop,
  saveProfilePhoto,
  type Crop,
} from '../lib/profilePhoto'
import { CROP_VIEW } from './PhotoCropDialog'

/**
 * Your own photo, and the pick → crop → save → remove flow around it (132,
 * 6 Oct). Lifted out of BusinessCardDialog on 11 Oct so #/profile can show and
 * change the same photo without a second copy of the flow (rule 11).
 *
 * The CALLER renders the pieces — a hidden file input on `inputRef`, the
 * PhotoCropDialog fed by `cropSource`, and wherever the face goes — because
 * where they sit in the tree matters (PhotoCropDialog's header: a dialog
 * nested in another dialog's React tree has its Tab bubble into that one's
 * focus trap). This hook owns state and acts, nothing visible.
 *
 * `enabled`: fetch the photo only once there is somebody to show it for — the
 * card dialog waits for its card, so a refusal has no face to fetch.
 */
export type MyPhotoFlow = {
  /** Null until my_photo() has answered; no photo button before then (SHL-8). */
  photo: { folder: string; url: string | null } | null
  photoUrl: string | null
  photoBusy: boolean
  photoError: string | null
  inputRef: React.RefObject<HTMLInputElement | null>
  /** Opens the file picker. */
  pick: () => void
  onPickPhoto: (file: File | undefined) => Promise<void>
  cropSource: ImageBitmap | null
  cropBusy: boolean
  cropError: string | null
  closeCrop: () => void
  onUseCrop: (crop: Crop) => Promise<void>
  onRemovePhoto: () => Promise<void>
  clearError: () => void
}

/** Said on window after a save or a removal, so a second holder of the flow
 *  (the card dialog under #/profile) shows the same face without refetching. */
const CHANGED = 'oneconetec:my-photo-changed'

export function useMyPhotoFlow(enabled: boolean, t: CardCopy): MyPhotoFlow {
  // `folder` is where an upload goes (from my_photo(), so the same tenant
  // set_my_photo checks); `url` is a data URL serving the face AND the .vcf.
  const [photo, setPhoto] = useState<{ folder: string; url: string | null } | null>(null)
  const [photoBusy, setPhotoBusy] = useState(false)
  const [photoError, setPhotoError] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  // The picked photo, decoded, while the crop dialog is open (6 Oct).
  const [cropSource, setCropSource] = useState<ImageBitmap | null>(null)
  const [cropBusy, setCropBusy] = useState(false)
  const [cropError, setCropError] = useState<string | null>(null)
  const mounted = useRef(true)
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  useEffect(() => {
    const on = (e: Event) => {
      const url = (e as CustomEvent<{ url: string | null }>).detail.url
      setPhoto((p) => (p ? { ...p, url } : p))
    }
    window.addEventListener(CHANGED, on)
    return () => window.removeEventListener(CHANGED, on)
  }, [])

  // A failure here costs the initials, never the screen around it.
  useEffect(() => {
    if (!enabled || photo) return
    let live = true
    loadMyPhoto()
      .then(async (mine) => {
        const url = mine.path ? await photoDataUrl(mine.path) : null
        if (live && mounted.current) setPhoto({ folder: mine.folder, url })
      })
      .catch(() => undefined)
    return () => {
      live = false
    }
    // `photo` is read, deliberately not a dependency: once loaded it is kept
    // current by onUseCrop and onRemovePhoto, not refetched.
  }, [enabled])

  // Decode HERE, before any cropper opens: a file the browser cannot draw
  // (HEIC on Chrome) is told so beside the button, not inside an empty frame.
  async function onPickPhoto(file: File | undefined) {
    if (inputRef.current) inputRef.current.value = '' // the same file twice must fire again
    if (!file || !photo) return
    setPhotoError(null)
    setCropError(null)
    try {
      const bitmap = await decodePhoto(file)
      if (mounted.current) setCropSource(bitmap)
      else bitmap.close()
    } catch {
      if (mounted.current) setPhotoError(t.photoNotImage)
    }
  }

  function closeCrop() {
    cropSource?.close()
    setCropSource(null)
    setCropError(null)
  }

  async function onUseCrop(crop: Crop) {
    if (!cropSource || !photo) return
    setCropBusy(true)
    setCropError(null)
    try {
      const blob = await renderCrop(cropSource, cropRect(CROP_VIEW, cropSource.width, cropSource.height, crop))
      const path = await saveProfilePhoto(photo.folder, blob)
      const url = await photoDataUrl(path)
      if (!mounted.current) return
      setPhoto({ folder: photo.folder, url })
      window.dispatchEvent(new CustomEvent(CHANGED, { detail: { url } }))
      closeCrop()
    } catch {
      if (mounted.current) setCropError(t.photoFailed)
    } finally {
      if (mounted.current) setCropBusy(false)
    }
  }

  async function onRemovePhoto() {
    if (!photo) return
    setPhotoBusy(true)
    setPhotoError(null)
    try {
      await removeProfilePhoto()
      if (mounted.current) setPhoto({ folder: photo.folder, url: null })
      window.dispatchEvent(new CustomEvent(CHANGED, { detail: { url: null } }))
    } catch {
      if (mounted.current) setPhotoError(t.photoFailed)
    } finally {
      if (mounted.current) setPhotoBusy(false)
    }
  }

  return {
    photo,
    photoUrl: photo?.url ?? null,
    photoBusy,
    photoError,
    inputRef,
    pick: () => inputRef.current?.click(),
    onPickPhoto,
    cropSource,
    cropBusy,
    cropError,
    closeCrop,
    onUseCrop,
    onRemovePhoto,
    clearError: () => setPhotoError(null),
  }
}
